<template>
  <div>
    <vue-headful :title="this.setPageTitle('scoringProfilesModelCreate')" />
    <loading :active="getLoading" :color="'blue'" is-full-page loader="spinner"> </loading>

    <b-container class="my-4">
      <!-- HEADER -->
      <custom-flex-header :parent-node-class="['my-4']">
        <template v-slot:left-content>
          <h1 class="mb-0 h4 font-weight-normal">
            {{ $t('scoringProfilesModelCreate.title') }}
          </h1>
        </template>
        <template v-slot:right-content>
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="onCancel">
            {{ $t('common.cancel') }}
          </b-button>
          <b-button size="sm" variant="primary" @click="onSave">
            {{ $t('common.save') }}
          </b-button>
        </template>
      </custom-flex-header>

      <!-- NAME -->
      <b-row class="mb-4">
        <b-col>
          <div class="d-flex">
            <div class="mr-2 w-50">
              <span>{{ $t('common.name') }}</span>
              <b-input
                size="sm"
                type="text"
                autocomplete="off"
                :formatter="nameFormatter"
                v-model="scoringProfileModelName"
                :placeholder="$t('scoringProfilesModelCreate.modelName')"
              ></b-input>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- TABS -->
      <b-row class="mb-4">
        <b-col>
          <b-tabs v-model="tabIndex" class="fairmat-primary-tabs" @activate-tab="onActivateTab">
            <b-tab v-for="(tab, index) in tabs" :key="index" :title="$t(tab.title)">
              <component v-bind:is="tab.type"></component>
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import TabScoringModelDrivers from './TabScoringModelDrivers.vue'
import TabScoringModelWeights from './TabScoringModelWeights.vue'
import { MODEL_TABS } from '../../settings/scoringProfilesSettings'
import { createNamespacedHelpers } from 'vuex'
import CustomFlexHeader from '../../components/Lib/CustomFlexHeader.vue'
import unsavedChangesMixin from '../../mixins/unsavedChanges'
import { scoringModelSnapshot } from '../../utils/unsavedChangesSnapshots'
const { mapActions, mapGetters, mapMutations } = createNamespacedHelpers('scoringProfilesModelsEditing')

export default {
  mixins: [unsavedChangesMixin],

  components: {
    CustomFlexHeader,
    TabScoringModelDrivers,
    TabScoringModelWeights,
  },

  data() {
    return {
      tabIndex: 0,
      tabs: MODEL_TABS,
      maxModelNameLength: 128,
    }
  },

  computed: {
    ...mapGetters([
      'getLoading',
      'getScoringProfileModelName',
      'getValidDriversFamiliesWeights',
      'getScoringProfileModelNameValidation',
      'getScoringProfileSelectedFamilies',
      'getModelFamiliesIndicatorsWeightsValidation',
    ]),

    scoringProfileModelName: {
      get() {
        return this.getScoringProfileModelName
      },
      set(newVal) {
        this.setScoringProfileModelName(this.nameFormatter(newVal))
      },
    },
  },

  methods: {
    unsavedChangesSnapshot() {
      return scoringModelSnapshot(this)
    },

    unsavedChangesReady() {
      return !this.getLoading
    },

    ...mapMutations(['setScoringProfileModelName']),
    ...mapActions(['saveScoringProfileModel', 'loadDriversFamiliesIndicators', 'setDefaultScoringProfileModel']),

    nameFormatter(value) {
      return value.substring(0, this.maxModelNameLength)
    },

    onActivateTab(tabIndex, prevTabIndex, evActiveTab) {},

    onSave() {
      let isValidName = this.getScoringProfileModelNameValidation
      const driversFamiliesWeights = this.getValidDriversFamiliesWeights
      const atLeastOneIndicatorMustBeSelected = driversFamiliesWeights.length > 0
      const isValidDriversFamiliesTotalWeight = driversFamiliesWeights.every((j) => j.isValidWeight)
      const getModelFamiliesIndicatorsWeightsValidation = this.getModelFamiliesIndicatorsWeightsValidation

      const results = this.$store.getters['scoringProfilesCommon/getScoringProfileDefinitionValidation']
      const modelName = this.getScoringProfileModelName
      // This check is needed to permit the value 'Custom profile' in the custom multiselect
      // included in TabScoringDefiniton.vue for the choice of the definition type and the
      // possibility to have a default value for custom scoring definition
      const invalidNames = [
        this.$i18n.messages['en'].scoringProfileDetails.customDefinition.toLowerCase(),
        this.$i18n.messages['it'].scoringProfileDetails.customDefinition.toLowerCase(),
      ]

      if (!isValidName) {
        this.$bvToast.toast(this.$t('scoringProfilesModelEdit.modalMessages.errorOnMissingName'), {
          title: this.$t('scoringProfilesModels.title'),
          variant: 'danger',
        })
      }

      if (invalidNames.includes(modelName.toLowerCase())) {
        isValidName = false
        this.$bvToast.toast(
          this.$t('scoringProfilesModelEdit.modalMessages.errorOnInvalidName', { invalidName: modelName }),
          {
            title: this.$t('scoringProfilesModels.title'),
            variant: 'danger',
          }
        )
      }

      if (!atLeastOneIndicatorMustBeSelected) {
        this.$bvToast.toast(this.$t('scoringProfilesModelEdit.modalMessages.atLeastOneIndicatorMustBeSelected'), {
          title: this.$t('scoringProfilesModels.title'),
          variant: 'danger',
        })
      }

      if (atLeastOneIndicatorMustBeSelected && !results.isValidScoringDefinitionTotalWeight) {
        this.$bvToast.toast(this.$t('scoringProfiles.errorOnTotalWeight'), {
          title: this.$t('scoringProfilesModels.title'),
          variant: 'danger',
        })
      }

      if (!results.isDriversWeightsGreaterThanZero) {
        this.$bvToast.toast(this.$t('scoringProfiles.weightsMustBeGreaterThan0'), {
          title: this.$t('scoringProfilesModels.title'),
          variant: 'danger',
        })
      }

      if (!getModelFamiliesIndicatorsWeightsValidation.validate) {
        getModelFamiliesIndicatorsWeightsValidation.indicators.forEach((item) => {
          const indicator = item.indicator
          const str = this.$t('drivers.codes.' + indicator.toUpperCase())
          this.$bvToast.toast(this.$t('scoringProfilesModelEdit.modalMessages.invalidDriverWeight', { driver: str }), {
            title: this.$t('scoringProfilesModels.title'),
            variant: 'danger',
          })
        })
      }

      if (!isValidDriversFamiliesTotalWeight) {
        const categories = driversFamiliesWeights.filter((k) => !k.isValidWeight)
        categories.forEach((z) => {
          const family = z.family
          const str = this.$t('drivers.categories.' + family.toUpperCase())
          this.$bvToast.toast(this.$t('scoringProfilesModelEdit.modalMessages.invalidDriverWeight', { driver: str }), {
            title: this.$t('scoringProfilesModels.title'),
            variant: 'danger',
          })
        })
      }

      const isValid =
        isValidName &&
        atLeastOneIndicatorMustBeSelected &&
        isValidDriversFamiliesTotalWeight &&
        results.isValidScoringDefinitionTotalWeight &&
        results.isDriversWeightsGreaterThanZero &&
        getModelFamiliesIndicatorsWeightsValidation.validate

      if (isValid) {
        this.saveScoringProfileModel()
          .then(() => {
            this.$router.push({ name: 'scoringProfilesModelsList' })
          })
          .catch(async (error) => {
            const err = await error.response
            const message = await err.text()
            if (message.includes('NameInUse')) {
              this.$bvToast.toast(this.$t('scoringProfiles.nameInUse'), {
                title: this.$t('scoringProfiles.title'),
                variant: 'danger',
              })
            } else if (message.includes('ScoringDefinitionWeightInvalid')) {
              this.$bvToast.toast(this.$t('scoringProfiles.scoringDefinitionWeightInvalid'), {
                title: this.$t('scoringProfiles.title'),
                variant: 'danger',
              })
            } else {
              this.$bvModal.msgBoxOk(this.$t('common.genericError'))
            }
          })
      }
    },

    onCancel() {
      this.$bvModal
        .msgBoxConfirm(this.$t('scoringProfilesList.modalMessages.confirmUnsaved'), {
          cancelTitle: this.$t('common.no'),
          okTitle: this.$t('common.yes'),
        })
        .then((value) => {
          // OK = value = true
          if (value) {
            this.$router.push({ name: 'scoringProfilesModelsList' })
          }
        })
    },
  },

  created() {
    this.setDefaultScoringProfileModel()
    this.loadDriversFamiliesIndicators()
  },
}
</script>

<style lang="scss" scoped></style>
